/** Layered tenant resolution for pages that own their `?tenant=` param by hand
 *  — the QuerySearch grids (Bookings, Orders, Delivery Logs, API Requests,
 *  Import Runs, Subscriptions, API Credentials, Notifications) and Sessions.
 *
 *  These read `sp.get('tenant')` directly instead of going through
 *  `useGridFilters`, so they need the same three-layer read spelled out:
 *
 *      URL ?tenant=   >   loaded preset   >   console scope
 *
 *  `getPresetParam` is normally `presets.getParam`, which already folds the URL
 *  and the preset together — pass it when the grid's presets cover the tenant
 *  (Sessions), omit it when they don't (the QuerySearch family stores its
 *  filter state inside `?q=`).
 *
 *  `raw` is deliberately the string, not an id: several of these pages carry
 *  their own sentinel in the same param ('system' for null-tenant rows,
 *  'null' for console-only tasks), and they keep decoding it themselves.
 *
 *  **One tenant or several.** The param is a comma list (`?tenant=12,845`), so
 *  `ids` / `values` are always the operators it names. By default the page is a
 *  one-tenant page: it adopts the scope only when the scope names exactly one
 *  operator, and `tenantId` / `value` are that one operator (`singleTenantId`,
 *  D5). With `multiple: true` the page takes the whole scope as its list.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { TenantPickerValue } from '@/components/TenantPicker/TenantPicker';

import { TENANT_ALL } from './scopeUrl';
import { formatTenantIds, parseTenantIds, singleTenantId } from './tenantIds';
import { useTenantScopeList } from './tenantScope';
import { useTenantScopeConsumer } from './useTenantScopeConsumer';

export interface UsePageTenant {
  /** Effective param value — URL, else preset, else scope. Sentinels intact. */
  raw: string | null;
  /** The operators `raw` names, ascending (empty for none or a sentinel). */
  ids: number[];
  /** `ids` as picker values, named where the scope knows them (else nameless —
   *  `TenantPicker` resolves the names itself). */
  values: TenantPickerValue[];
  /** The page's one operator: `raw` names exactly one, else null (none, several,
   *  or a sentinel). */
  tenantId: number | null;
  /** Picker-ready `tenantId`, with the name filled in when the scope supplies it. */
  value: TenantPickerValue | null;
  /** True when the value came from the console scope rather than this page. */
  fromScope: boolean;
  /** Write the page's own tenant. Clearing under a scope writes the `all`
   *  sentinel, so the filter is actually clearable (a delete would fall back to
   *  the scope). */
  setTenant: (t: TenantPickerValue | null) => void;
  /** Write the page's own tenants (a multiple picker's value); `[]` clears. */
  setTenants: (ts: readonly TenantPickerValue[]) => void;
  /** Same write path for a raw value (a page sentinel, or a quick-filter id). */
  setRaw: (value: string | null) => void;
  /** Apply the same write rule to a URLSearchParams the caller is already
   *  building — for pages that change several params in one go (Anatomy clears
   *  the selected notification when the tenant changes). */
  writeTo: (next: URLSearchParams, value: string | null) => void;
}

export interface UsePageTenantOptions {
  param?: string;
  /** Usually `presets.getParam` — the URL+preset read, when presets cover it. */
  getPresetParam?: (param: string) => string | null;
  /** Usually `presets.beforeUrlWrite` — only when presets cover the param. */
  beforeWrite?: (next: URLSearchParams) => void;
  /** Withhold the scope layer: the page has an explicit tenant elsewhere (a
   *  `tenant=` condition in `?q=`, which a preset may have supplied). */
  suppressScope?: boolean;
  /** The page takes several tenants: it adopts the whole scope as its list.
   *  Without it, a scope of several operators does not apply here. */
  multiple?: boolean;
}

export function usePageTenant(options: UsePageTenantOptions = {}): UsePageTenant {
  const { param = 'tenant', getPresetParam, beforeWrite, suppressScope = false, multiple = false } = options;
  const [sp, setSp] = useSearchParams();
  const scopeList = useTenantScopeList();
  useTenantScopeConsumer(true, multiple ? 'multi' : 'single');

  // What the scope contributes here: the list (multiple), or its one operator.
  const scopeRaw = multiple
    ? formatTenantIds(scopeList.map((s) => s.id))
    : scopeList.length === 1
      ? String(scopeList[0]?.id)
      : null;

  const pageRaw = getPresetParam ? getPresetParam(param) : sp.get(param);
  const fromScope = scopeRaw !== null && !suppressScope && !pageRaw;
  const raw = fromScope ? scopeRaw : pageRaw;

  const idsKey = formatTenantIds(parseTenantIds(raw)) ?? '';
  const values = useMemo<TenantPickerValue[]>(
    () =>
      parseTenantIds(idsKey).map((id) => {
        const s = scopeList.find((x) => x.id === id);
        // Nameless — TenantPicker resolves the label itself.
        return s ?? { id, name: '' };
      }),
    [idsKey, scopeList],
  );
  const ids = useMemo(() => values.map((v) => v.id), [values]);
  const tenantId = singleTenantId(ids);
  const value = tenantId === null ? null : (values[0] ?? null);

  const writeTo = useCallback(
    (params: URLSearchParams, next: string | null) => {
      if (next) params.set(param, next);
      else if (scopeRaw !== null) params.set(param, TENANT_ALL);
      else params.delete(param);
    },
    [param, scopeRaw],
  );

  const setRaw = useCallback(
    (next: string | null) => {
      const params = new URLSearchParams(sp);
      beforeWrite?.(params);
      writeTo(params, next);
      setSp(params, { replace: true });
    },
    [sp, setSp, beforeWrite, writeTo],
  );

  const setTenant = useCallback(
    (t: TenantPickerValue | null) => setRaw(t ? String(t.id) : null),
    [setRaw],
  );

  const setTenants = useCallback(
    (ts: readonly TenantPickerValue[]) => setRaw(formatTenantIds(ts.map((t) => t.id))),
    [setRaw],
  );

  return { raw, ids, values, tenantId, value, fromScope, setTenant, setTenants, setRaw, writeTo };
}
